/* ============================================================
   Corte livre: a conta das alças

   O cortador do site e o do app são o mesmo gesto — a foto inteira na
   tela, um retângulo com alças nos cantos e nos lados, arrastar por
   dentro move — e a conta mora aqui uma vez só. As duas telas só
   traduzem dedo em `dx, dy` e desenham o retângulo que volta.

   Tudo em coordenadas da foto COMO ESTÁ DESENHADA (0..largura,
   0..altura da imagem na tela). A tela converte para fração no fim.
   ============================================================ */

export interface Retangulo { x: number; y: number; w: number; h: number }

/** Qual parte do retângulo o dedo pegou. */
export type Alca = "tl" | "t" | "tr" | "r" | "br" | "b" | "bl" | "l" | "meio";

/** Menor lado do corte, em pixels de tela: menos que isso o dedo não acerta mais as alças. */
export const LADO_MINIMO_DO_CORTE = 48;

/**
 * O retângulo depois de arrastar `alca` por `dx, dy`, a partir de
 * `inicio` (o retângulo de quando o dedo encostou — sempre relativo ao
 * começo do gesto, para não acumular erro).
 *
 * - nunca sai da foto (`limite`);
 * - nunca fica menor que `minimo`;
 * - com `razao` (largura ÷ altura, ex.: 1 na foto de perfil) os cantos
 *   mantêm a proporção e os lados mexem os dois eixos juntos.
 */
export function moverAlca(
  inicio: Retangulo, alca: Alca, dx: number, dy: number,
  limite: { w: number; h: number }, minimo = LADO_MINIMO_DO_CORTE, razao?: number,
): Retangulo {
  const min = Math.min(minimo, limite.w, limite.h);
  if (alca === "meio") {
    return {
      ...inicio,
      x: Math.min(limite.w - inicio.w, Math.max(0, inicio.x + dx)),
      y: Math.min(limite.h - inicio.h, Math.max(0, inicio.y + dy)),
    };
  }
  if (razao) return moverComRazao(inicio, alca, dx, dy, limite, min, razao);

  let x1 = inicio.x, y1 = inicio.y, x2 = inicio.x + inicio.w, y2 = inicio.y + inicio.h;
  if (alca.includes("l") ) x1 = Math.min(x2 - min, Math.max(0, x1 + dx));
  if (alca.includes("r")) x2 = Math.max(x1 + min, Math.min(limite.w, x2 + dx));
  if (alca.includes("t")) y1 = Math.min(y2 - min, Math.max(0, y1 + dy));
  if (alca.includes("b")) y2 = Math.max(y1 + min, Math.min(limite.h, y2 + dy));
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

/**
 * Proporção travada: o canto oposto à alça fica parado (nos lados, o
 * meio do lado oposto), e o tamanho segue o eixo que o dedo mais andou.
 */
function moverComRazao(
  r: Retangulo, alca: Alca, dx: number, dy: number,
  limite: { w: number; h: number }, min: number, razao: number,
): Retangulo {
  const esq = alca.includes("l"), dir = alca.includes("r");
  const cima = alca.includes("t"), baixo = alca.includes("b");
  // Quanto a largura quer mudar, pelo eixo que o dedo mais andou.
  const pelaLargura = esq ? -dx : dir ? dx : 0;
  const pelaAltura = (cima ? -dy : baixo ? dy : 0) * razao;
  const delta = Math.abs(pelaLargura) >= Math.abs(pelaAltura) ? pelaLargura : pelaAltura;
  let w = Math.max(min * Math.max(1, razao), r.w + delta);

  // O ponto que fica parado.
  const ancoraX = esq ? r.x + r.w : dir ? r.x : r.x + r.w / 2;
  const ancoraY = cima ? r.y + r.h : baixo ? r.y : r.y + r.h / 2;

  // Quanto cabe a partir da âncora, em cada eixo.
  const cabeW = esq ? ancoraX : dir ? limite.w - ancoraX : 2 * Math.min(ancoraX, limite.w - ancoraX);
  const cabeH = cima ? ancoraY : baixo ? limite.h - ancoraY : 2 * Math.min(ancoraY, limite.h - ancoraY);
  w = Math.min(w, cabeW, cabeH * razao);
  const h = w / razao;

  const x = esq ? ancoraX - w : dir ? ancoraX : ancoraX - w / 2;
  const y = cima ? ancoraY - h : baixo ? ancoraY : ancoraY - h / 2;
  return { x, y, w, h };
}

/**
 * O retângulo inicial: a foto inteira (corte livre) ou o maior quadrado
 * — ou a maior caixa na `razao` — centralizado.
 */
export function corteInicial(limite: { w: number; h: number }, razao?: number): Retangulo {
  if (!razao) return { x: 0, y: 0, w: limite.w, h: limite.h };
  const w = Math.min(limite.w, limite.h * razao);
  const h = w / razao;
  return { x: (limite.w - w) / 2, y: (limite.h - h) / 2, w, h };
}

/** O retângulo em frações da foto (0..1), que é o que o corte de verdade usa. */
export const emFracao = (r: Retangulo, limite: { w: number; h: number }): Retangulo => ({
  x: Math.max(0, r.x / limite.w), y: Math.max(0, r.y / limite.h),
  w: Math.min(1, r.w / limite.w), h: Math.min(1, r.h / limite.h),
});

/** Qual alça está perto do ponto (para o site, que recebe o toque no quadro todo). */
export function alcaNoPonto(r: Retangulo, px: number, py: number, raio = 28): Alca | null {
  const perto = (a: number, b: number) => Math.abs(a - b) <= raio;
  const dentroX = px >= r.x - raio && px <= r.x + r.w + raio;
  const dentroY = py >= r.y - raio && py <= r.y + r.h + raio;
  if (!dentroX || !dentroY) return null;
  const l = perto(px, r.x), rr = perto(px, r.x + r.w), t = perto(py, r.y), b = perto(py, r.y + r.h);
  if (t && l) return "tl";
  if (t && rr) return "tr";
  if (b && l) return "bl";
  if (b && rr) return "br";
  if (t) return "t";
  if (b) return "b";
  if (l) return "l";
  if (rr) return "r";
  if (px > r.x && px < r.x + r.w && py > r.y && py < r.y + r.h) return "meio";
  return null;
}
